import { get } from 'lodash';
import moment from 'moment';
import { FORM_ERROR } from 'final-form';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import datesInRange from 'common/utils/Dates/datesInRange';
import nearestDay from 'common/utils/Dates/nearestDay';
import dollarsToCents from 'common/utils/Money/dollarsToCents';

export const DATE_TYPE_VALUES = {
  SINGLE_DATE: 'single',
  RANGE_DATE: 'range',
};

export const DATE_TYPE_OPTIONS = [
  { label: 'Single Date', value: DATE_TYPE_VALUES.SINGLE_DATE },
  { label: 'Date Range', value: DATE_TYPE_VALUES.RANGE_DATE },
];

export const FORM_ERRORS = {
  AMOUNT_EXCEEDED: 'amount_exceeded',
  AUTHORIZED_AMOUNT_BELOW_INVOICED: 'authorized_amount_below_invoiced',
  DATES_OUT_OF_RANGE: 'dates_out_of_range',
  INVOICES_OUTSIDE_AUTH_DATES: 'invoices_outside_auth_dates',
};

export const isSingleDate = (authorization, prefix) => (
  authorization[`${prefix}_starts_at`] === authorization[`${prefix}_ends_at`]
);

export const buildFormInitialValues = (authorization, { prefix, isUnitBased, extraFields = {} }) => ({
  authorized_amount: isUnitBased ?
    authorization[`${prefix}_unit_amount`] :
    authorization[`${prefix}_cents`] / 100,
  date_type: isSingleDate(authorization, prefix) ?
    DATE_TYPE_VALUES.SINGLE_DATE :
    DATE_TYPE_VALUES.RANGE_DATE,
  single_date: Date.parse(authorization[`${prefix}_ends_at`]) / 1000,
  range_date_start: Date.parse(authorization[`${prefix}_starts_at`]) / 1000,
  range_date_end: Date.parse(authorization[`${prefix}_ends_at`]) / 1000,
  ...extraFields,
});

export const closeModal = (modalRef, reset) => {
  if (modalRef.current) {
    reset();
    modalRef.current.closeDialog();
  }
};

const parseRelationships = (relationships = {}) => Object.entries(relationships).reduce((acc, [key, value]) => ({
  ...acc,
  [key]: value?.data,
}), {});

export const getPersonData = (data, authorization, includeInternalNonConsentedClients) => {
  if (includeInternalNonConsentedClients) {
    const [includedPerson] = get(data, 'data.included', []);
    return includedPerson ? {
      id: includedPerson.id,
      ...includedPerson.attributes,
      ...parseRelationships(includedPerson.relationships),
    } : {};
  }
  return get(authorization, 'person', {});
};

export const datesCoveredByInsurance = (formValues, authorization) => {
  const { insurance } = authorization;
  const startDate = moment.unix(formValues.range_date_start).utc();
  const endDate = moment.unix(formValues.range_date_end).utc();
  const singleDate = moment.unix(formValues.single_date).utc();
  const enrolledAt = nearestDay(insurance.enrolled_at);
  const expiredAt = insurance.expired_at && nearestDay(insurance.expired_at);
  return formValues.date_type === DATE_TYPE_VALUES.RANGE_DATE ?
    datesInRange(enrolledAt, expiredAt, startDate, endDate) :
    datesInRange(enrolledAt, expiredAt, singleDate);
};

export const isDateEntered = (values) => (
  (values.date_type === DATE_TYPE_VALUES.RANGE_DATE &&
    values.range_date_start && values.range_date_end) ||
  (values.date_type === DATE_TYPE_VALUES.SINGLE_DATE && values.single_date)
);

export const getDisplayDates = (authorization) => {
  const dateRequestedStartsAt = formatShortDate(authorization.requested_starts_at);
  const dateRequestedEndsAt = formatShortDate(authorization.requested_ends_at);
  const isSingleDateRequested = authorization.requested_starts_at === authorization.requested_ends_at;

  return {
    dateRequestedStartsAt,
    dateRequestedEndsAt,
    isSingleDateRequested,
  };
};

const getNormalizedUtcDayRange = ({ epochValue, isoDate, isRangeEnd = false }) => {
  let date;

  if (epochValue !== undefined && epochValue !== null) {
    date = moment.unix(epochValue).utc();
  } else {
    date = moment.utc(isoDate);
  }

  return isRangeEnd ? date.endOf('day') : date.startOf('day');
};

export function validateAuthorizationForm(
  values,
  {
    isAmountExceeded,
    isUnitBased,
    isInvoicedSpendLoaded,
    totalInvoicedSpend,
    invoicedSpendRecords = [],
    authorization,
  },
) {
  let areDatesSelected;
  if (values.date_type === DATE_TYPE_VALUES.SINGLE_DATE) {
    areDatesSelected = !!values.single_date;
  } else {
    areDatesSelected = !!values.range_date_start && !!values.range_date_end;
  }

  if (isAmountExceeded && isDateEntered(values)) {
    return { [FORM_ERROR]: FORM_ERRORS.AMOUNT_EXCEEDED };
  }

  if (values.authorized_amount && isInvoicedSpendLoaded) {
    let parsedAuthorizedAmount;
    if (isUnitBased) {
      parsedAuthorizedAmount = parseInt(values.authorized_amount, 10);
    } else {
      parsedAuthorizedAmount = dollarsToCents(values.authorized_amount);
    }

    if (parsedAuthorizedAmount < totalInvoicedSpend) {
      return { [FORM_ERROR]: FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED };
    }
  }

  if (areDatesSelected && invoicedSpendRecords.length > 0) {
    const formStartDate = values.date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
      getNormalizedUtcDayRange({ epochValue: values.single_date }) :
      getNormalizedUtcDayRange({ epochValue: values.range_date_start });
    const formEndDate = values.date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
      getNormalizedUtcDayRange({ epochValue: values.single_date, isRangeEnd: true }) :
      getNormalizedUtcDayRange({
        epochValue: values.range_date_end || values.range_date_start,
        isRangeEnd: true,
      });

    const hasInvoiceOutsideRange = invoicedSpendRecords.some((record) => {
      const recordStart = getNormalizedUtcDayRange({ isoDate: record.service_starts_at });
      const recordEnd = getNormalizedUtcDayRange({
        isoDate: record.service_ends_at || record.service_starts_at,
        isRangeEnd: true,
      });

      return recordStart.isBefore(formStartDate) || recordEnd.isAfter(formEndDate);
    });

    if (hasInvoiceOutsideRange) {
      return { [FORM_ERROR]: FORM_ERRORS.INVOICES_OUTSIDE_AUTH_DATES };
    }
  }

  if (areDatesSelected && !datesCoveredByInsurance(values, authorization)) {
    return { [FORM_ERROR]: FORM_ERRORS.DATES_OUT_OF_RANGE };
  }

  return {};
}

export const isOriginal = (serviceAuthorization) => (
  serviceAuthorization.original_service_authorization?.id === serviceAuthorization.id
);

export const isEdition = (serviceAuthorization) => (
  !isOriginal(serviceAuthorization)
);

export const isRequestedEdition = (serviceAuthorization) => (
  isEdition(serviceAuthorization) && serviceAuthorization.state === 'requested'
);

export const getOriginalId = (serviceAuthorization) => (
  serviceAuthorization.original_service_authorization?.id
);

export const hasRequestedOrInReviewEdition = (serviceAuthorization) => {
  const lineage = serviceAuthorization.lineage || [];
  const isSingleSelfLineage = lineage.length === 1 && lineage[0].id === serviceAuthorization.id;

  if (isSingleSelfLineage) return false;

  return lineage.some((auth) => (auth.state === 'requested' || auth.state === 'in_review'));
};
